import { Check } from '@phosphor-icons/react';
import type { ReactNode } from 'react';
import {
  Checkbox as AriaCheckbox,
  type CheckboxProps as AriaCheckboxProps,
} from 'react-aria-components';
import { cn } from '../lib/cn';
import { Icon } from './Icon';

export interface CheckboxProps extends Omit<AriaCheckboxProps, 'className' | 'children'> {
  /** The whole text is the touch target – consents can be read and ticked in one go. */
  children: ReactNode;
  className?: string;
}

/** A single yes/no tick with its label next to it (consents, options). */
export function Checkbox({ children, className, ...props }: CheckboxProps) {
  return (
    <AriaCheckbox
      {...props}
      className={cn(
        'group flex min-h-touch-min cursor-pointer items-start gap-gap-md py-inset-xs outline-none',
        'data-disabled:cursor-not-allowed data-disabled:opacity-50',
        className,
      )}
    >
      {({ isSelected }) => (
        <>
          <span
            aria-hidden
            className={cn(
              'grid size-icon-lg shrink-0 place-items-center rounded-control border-2 border-border-default bg-surface-raised',
              'group-data-selected:border-action-primary group-data-selected:bg-action-primary group-data-selected:text-text-on-action',
              'group-data-focus-visible:outline-3 group-data-focus-visible:outline-border-focus',
            )}
          >
            {isSelected && <Icon icon={Check} size="sm" weight="bold" />}
          </span>
          <span className="text-md text-text-primary">{children}</span>
        </>
      )}
    </AriaCheckbox>
  );
}
